// Multi-tenant configuration system
import tierPresets from "../../config/tiers.json";
import ptfcConfig from "../../config/tenants/ptfc.json";
import club2Config from "../../config/tenants/club2.json";
import kfcConfig from "../../config/tenants/kfc.json";
import dfcConfig from "../../config/tenants/dfc.json";
import stjfcConfig from "../../config/tenants/stjfc.json";
import ffcConfig from "../../config/tenants/ffc.json";
import srfcConfig from "../../config/tenants/srfc.json";
import lfcConfig from "../../config/tenants/lfc.json";
import stmfcConfig from "../../config/tenants/stmfc.json";
import bjfcConfig from "../../config/tenants/bjfc.json";

const configs = {
  ptfc: ptfcConfig,
  club2: club2Config,
  kfc: kfcConfig,
  dfc: dfcConfig,
  stjfc: stjfcConfig,
  ffc: ffcConfig,
  srfc: srfcConfig,
  lfc: lfcConfig,
  stmfc: stmfcConfig,
  bjfc: bjfcConfig,
  // Add other club configs as they are created
};

// Get tenant from environment variable. In production the tenant MUST be set
// at build time (enforced in vue.config.js) — we do NOT fall back to ptfc,
// because a mis-built bundle should white-screen loudly rather than silently
// ship Partick Thistle's branding to another club. Local dev (`npm run serve`)
// falls back to ptfc for convenience.
const currentTenant =
  process.env.VUE_APP_TENANT ||
  (process.env.NODE_ENV === "production" ? "" : "ptfc");

// Validate tenant exists
if (!configs[currentTenant]) {
  console.error(
    `Invalid tenant: "${currentTenant}". Available tenants:`,
    Object.keys(configs),
  );
  throw new Error(
    `Tenant configuration not found for: "${currentTenant}". ` +
      `A production build must set VUE_APP_TENANT — use an npm run build:<club> script.`,
  );
}

const tenantConfig = configs[currentTenant];

// Resolve feature flags from the tenant's tier preset. The preset supplies
// the base flags; any flags set in the tenant's own `features` block override
// per-flag. Tenants without a `tier` are treated as pro.
const currentTier = tenantConfig.tier || "pro";

if (!tierPresets[currentTier]) {
  throw new Error(
    `Unknown tier "${currentTier}" for tenant: ${currentTenant}. Available tiers: ${Object.keys(
      tierPresets,
    )
      .filter((key) => key !== "_comment")
      .join(", ")}`,
  );
}

tenantConfig.features = {
  ...tierPresets[currentTier].features,
  ...(tenantConfig.features || {}),
};

// Export the current tenant configuration
export default tenantConfig;

// Tier utilities
export const getTier = () => currentTier;
export const isGrassroots = () => currentTier === "grassroots";

// Export utility functions
export const getTenantId = () => tenantConfig.id;
export const getTenantName = () => tenantConfig.name;
export const getTenantNameShort = () => tenantConfig.nameShort;
export const getClubId = () => tenantConfig.clubId;
export const getAppName = () => tenantConfig.appName;
export const getDomain = () => tenantConfig.domain;

// Feature flag utility
export const isFeatureEnabled = (feature) => {
  return tenantConfig.features?.[feature] ?? false;
};

// Payments currency (tenant payments.currency, e.g. "aud" for Australian
// clubs — must match the backend's Stripe price currency). ISO code
// uppercased for Intl.NumberFormat.
export const getPaymentsCurrency = () =>
  (tenantConfig.payments?.currency || "gbp").toUpperCase();

export const getCurrencySymbol = () =>
  ({ GBP: "£", AUD: "$", USD: "$", EUR: "€", NZD: "$" })[
    getPaymentsCurrency()
  ] ?? "£";

// Sports voucher scheme (e.g. SA Sports Vouchers for Australian clubs).
// Null when the tenant has none — voucher UI hides entirely.
export const getVoucherConfig = () => {
  const v = tenantConfig.payments?.vouchers;
  if (!v || !v.amount_pence) return null;
  return { label: v.label || "Sports Voucher", amount_pence: v.amount_pence };
};

// Platform billing model — the Apple-model grassroots per-player subscription:
// { model: "per_player", currency, unitAmountPence, capPence } or null.
// Config only for now; the engine that actually bills clubs isn't built yet.
// See config/tiers.json + GRASSROOTS_PLAN.md §3a.
/**
 * Legal wording for the privacy / terms / deletion pages and the
 * registration consents. UK GDPR is the default; an AU tenant sets a
 * `legal` block in its JSON (see srfc.json). Any key can be overridden.
 */
const LEGAL_DEFAULTS = {
  region: "UK",
  privacyLaw:
    "the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018",
  privacyLawShort: "UK GDPR",
  regulator: "the UK Information Commissioner's Office (ICO)",
  governingLaw: "the laws of Scotland and the United Kingdom",
  courts: "the Scottish courts",
  contactUrl: "",
  insuranceNote: "",
};
const LEGAL_AU = {
  region: "AU",
  privacyLaw:
    "the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs)",
  privacyLawShort: "Australian privacy law",
  regulator: "the Office of the Australian Information Commissioner (OAIC)",
  governingLaw: "the laws of South Australia and the Commonwealth of Australia",
  courts: "the courts of South Australia",
};
export const getLegalConfig = () => {
  const base =
    (tenantConfig.payments?.country || "").toUpperCase() === "AU"
      ? { ...LEGAL_DEFAULTS, ...LEGAL_AU }
      : LEGAL_DEFAULTS;
  return { ...base, ...(tenantConfig.legal || {}) };
};

export const getPlatformBilling = () =>
  tenantConfig.payments?.platformBilling ?? null;

// Season window (mirrors TenantConfig::getSeasonWindow in PHP).
// style "cross"    — UK two-year seasons (Jul–Jun, labelled "2026-27").
// style "calendar" — single-year seasons (Australia: played Mar–Sep,
//                    labelled "2026"; data window is the full year).
// Effective season block. Australian tenants that don't define their own
// `season` default to a cross-year Oct→Sep season (labelled "2025-26") — the
// AU grassroots norm. Mirror of TenantConfig::getSeasonConfig() in PHP.
const resolveSeason = () => {
  const s = tenantConfig.season;
  const isAU = (tenantConfig.payments?.country ?? "").toUpperCase() === "AU";
  if ((!s || Object.keys(s).length === 0) && isAU) {
    return { style: "cross", startMonth: 10, startDay: 1, endMonth: 9, endDay: 30 };
  }
  return s ?? {};
};

export const isCalendarSeason = () => resolveSeason().style === "calendar";

export const getSeasonWindow = (date = new Date()) => {
  const s = resolveSeason();
  const pad = (n) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = date.getMonth() + 1;

  if (isCalendarSeason()) {
    return {
      startDate: `${year}-${pad(s.startMonth ?? 1)}-${pad(s.startDay ?? 1)}`,
      endDate: `${year}-${pad(s.endMonth ?? 12)}-${pad(s.endDay ?? 31)}`,
      label: String(year),
    };
  }

  const startMonth = s.startMonth ?? 7;
  const startYear = month >= startMonth ? year : year - 1;
  // labelBy: 'end' → "2027" (year it ends, AU Oct→Sep), 'start' → "2026",
  // else 'range' → "2026-27".
  const label =
    s.labelBy === "end"
      ? String(startYear + 1)
      : s.labelBy === "start"
      ? String(startYear)
      : `${startYear}-${pad((startYear + 1) % 100)}`;
  return {
    startDate: `${startYear}-${pad(startMonth)}-${pad(s.startDay ?? 1)}`,
    endDate: `${startYear + 1}-${pad(s.endMonth ?? 6)}-${pad(s.endDay ?? 30)}`,
    label,
  };
};

// Integration utilities
export const getIntegrationConfig = (integration) => {
  return tenantConfig.integrations?.[integration] ?? {};
};

export const isIntegrationEnabled = (integration) => {
  return tenantConfig.integrations?.[integration]?.enabled ?? false;
};

// Asset utilities
export const getAssetPath = (assetType) => {
  return tenantConfig.assets?.[assetType] ?? "";
};

// Database configuration
export const getDatabaseConfig = () => tenantConfig.database;

// Manifest configuration
export const getManifestConfig = () => tenantConfig.manifest;
